import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import List from '@mui/material/List';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import ListSkeletonComponent from './List-skeleton.component';
import SubuserItemComponent from './Subuser-item.component';
import AddSubuserContainer from '../Add-subuser/Add-subuser.container';
import AddDeviceToSubuserContainer from '../Add-device-to-subuser/Add-device-to-subuser.container';
import EditSubuserContainer from '../Edit-subuser/Edit-subuser.container';
import DeleteSubuserContainer from '../Delete-subuser/Delete-subuser.container';
import SubuserDevicesContainer from '../Subuser-devices/Subuser-devices.container';
import EditSubuserRightsContainer from '../Edit-subuser-rights/Edit-subuser-rights.container';
import RemoveDeviceContainer from '../Remove-device/Remove-device.container';
import useDictionary from '@ui/hooks/useDictionary';
import { IProps } from './IProps';
import { IDevice } from './IDevice';
import { IStoredDevice } from '@store/Devices.store';

type deviceMap = {
    [key: string]: IStoredDevice;
};

const Component: React.FC<IProps> = ({
    subusersDevices,
    devices,
    loading,
    subusersList,
    addSubuserVisible,
    editSubuserVisible,
    deleteSubuserVisible,
    subuserDevicesVisible,
    addDeviceToSubuserVisible,
    editSubuserRightsVisible,
    deviceBtnHover,
    deleteDeviceVisible,
    visibleList,
    onAddSubuserOpen,
    onAddSubuserClose,
    onEditSubuserOpen,
    onEditSubuserClose,
    onDeleteSubuserOpen,
    onDeleteSubuserClose,
    onSubuserDevicesClose,
    onAddDeviceToSubuserOpen,
    onAddDeviceToSubuserClose,
    onEditSubuserRightsOpen,
    onEditSubuserRightsClose,
    onDeviceBtnHoverStart,
    onDeviceBtnHoverStop,
    onDeleteDeviceOpen,
    onDeleteDeviceClose,
    onSubuserClick,
}: IProps) => {
    const getWord = useDictionary();
    const devicesMap: deviceMap = useMemo(
        () =>
            devices.reduce(
                (acc, device) => ({ ...acc, [`${device.imei}`]: device }),
                {} as deviceMap,
            ),
        [devices],
    );
    const subuserPipe = (name: string) => {
        const [subuserItem] = subusersDevices.filter(
            ({ subuser }) => subuser === name,
        );
        let deviceList: string[] = [];
        if (subuserItem && subuserItem.devices)
            deviceList = subuserItem.devices;
        let list: IDevice[] = [];
        const visible = visibleList[name];
        list = deviceList
            .filter((imei) => {
                const device = devicesMap[imei];
                return device && device.name;
            })
            .map((imei) => {
                const device = devicesMap[imei];
                return { name: device.name, imei };
            });
        return (
            <SubuserItemComponent
                key={name}
                id={name}
                loading={loading}
                name={name}
                deviceList={list}
                visible={visible}
                deviceBtnHover={deviceBtnHover}
                onAddDeviceToSubuserOpen={onAddDeviceToSubuserOpen}
                onEditSubuserOpen={onEditSubuserOpen}
                onDeleteSubuserOpen={onDeleteSubuserOpen}
                onClick={onSubuserClick}
                onEditSubuserRightsOpen={onEditSubuserRightsOpen}
                onDeleteDeviceOpen={onDeleteDeviceOpen}
                onDeviceBtnHoverStart={onDeviceBtnHoverStart}
                onDeviceBtnHoverStop={onDeviceBtnHoverStop}
            />
        );
    };
    const getSubusersItems = () => {
        if (subusersList.length > 0) {
            return subusersList.map(subuserPipe);
        } else return <></>;
    };
    const subusersItems = getSubusersItems();
    const skeleton = new Array(5)
        .fill(0)
        .map((v, i) => <ListSkeletonComponent key={i} />);
    const items =
        subusersList.length === 0 && loading ? skeleton : subusersItems;

    return (
        <>
            <Box className="subusers" component="section">
                <List className="subusers__list" component="ul">
                    {items}
                </List>
                <Box className="subusers__btn-bar">
                    <Tooltip
                        classes={{
                            popper: 'subusers__item-menu-tooltip-popper',
                            tooltip: 'subusers__item-menu-tooltip',
                        }}
                        title={getWord('addSubuserTitle')}
                        placement="top"
                    >
                        <Fab
                            className="subusers__btn"
                            color="primary"
                            onClick={onAddSubuserOpen}
                        >
                            <AddIcon />
                        </Fab>
                    </Tooltip>
                </Box>
            </Box>
            <Modal open={addSubuserVisible} onClose={onAddSubuserClose}>
                <AddSubuserContainer onClose={onAddSubuserClose} />
            </Modal>
            <Modal open={editSubuserVisible} onClose={onEditSubuserClose}>
                <EditSubuserContainer onClose={onEditSubuserClose} />
            </Modal>
            <Modal open={deleteSubuserVisible} onClose={onDeleteSubuserClose}>
                <DeleteSubuserContainer onClose={onDeleteSubuserClose} />
            </Modal>
            <Modal open={subuserDevicesVisible} onClose={onSubuserDevicesClose}>
                <SubuserDevicesContainer onClose={onSubuserDevicesClose} />
            </Modal>
            <Modal
                open={addDeviceToSubuserVisible}
                onClose={onAddDeviceToSubuserClose}
            >
                <AddDeviceToSubuserContainer
                    onClose={onAddDeviceToSubuserClose}
                />
            </Modal>
            <Modal
                open={editSubuserRightsVisible}
                onClose={onEditSubuserRightsClose}
            >
                <EditSubuserRightsContainer
                    onClose={onEditSubuserRightsClose}
                />
            </Modal>
            <Modal open={deleteDeviceVisible} onClose={onDeleteDeviceClose}>
                <RemoveDeviceContainer onClose={onDeleteDeviceClose} />
            </Modal>
        </>
    );
};

const SubusersComponent = React.memo(Component);

export default SubusersComponent;
